<script setup lang="ts">
import { useToast } from '@/composables/useToast';
import { useI18n } from '@/i18n';
import { logError } from '@/lib/logError';
import { useAccountsStore } from '@/stores/accounts';
import { useSidebarStore } from '@/stores/sidebar';
import { useUserStore } from '@/stores/user';
import { Badge, Dropdown, LayoutGap, Menu, MenuDivider, MenuHeading, MenuItem } from '@dabble/quillui';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useRoute } from 'vue-router';

const { t } = useI18n();
const sidebarStore = useSidebarStore();
const accountsStore = useAccountsStore();
const userStore = useUserStore();
const route = useRoute();
const { options } = storeToRefs(sidebarStore);
const { otherAccounts, isImpersonating } = storeToRefs(accountsStore);
const { showToast } = useToast();

const impersonatedName = computed(() => userStore.user.name || userStore.user.email || userStore.user.uid || '');

async function selectOption(index: number) {
  const option = options.value[index];
  if (option.preAction) {
    try {
      await option.preAction();
    } catch (error) {
      logError('Error in preAction', error);
      showToast('danger', t('sidebar_error_preaction', { name: t(option.textKey) }));
      return;
    }
  }
  sidebarStore.selectOption(index);
}

function switchTo(uid: string) {
  accountsStore.switchAccount(uid);
}

function addAnotherAccount() {
  const redirect = encodeURIComponent(`${location.origin}${route.fullPath}`);
  window.location.href = `/auth/login?redirect=${redirect}`;
}

function stopImpersonating() {
  accountsStore.stopImpersonating();
}

function handleHelp() {
  window.open('https://help.dabblewriter.com/en/', '_blank');
}

function handleStoryCraftCafe() {
  window.open('https://www.storycraft.cafe/feed', '_blank');
}

function requestAFeature() {
  window.open('https://dabble.featureupvote.com/', '_blank');
}
</script>

<template>
  <div class="sidebar">
    <LayoutGap direction="column" fill class="sidebar__inner">
      <button
        v-if="isImpersonating"
        type="button"
        class="sidebar__impersonate"
        :title="t('account_stop_impersonating')"
        :aria-label="t('account_stop_impersonating')"
        @click="accountsStore.stopImpersonatingToAdmin"
      >
        <Badge variant="warning" size="small" icon-right="sign-out">
          {{ t('account_impersonating_badge', { name: impersonatedName }) }}
        </Badge>
      </button>
      <Menu finish="primary" class="sidebar__menu">
        <MenuHeading>{{ t('sidebar_title') }}</MenuHeading>
        <template v-for="(option, index) in options" :key="index">
          <Dropdown v-if="option.isSwitchAccount" placement="right-start">
            <template #action="{ onClick }">
              <MenuItem :icon-left="option.icon" icon-right="chevron-right" @click="onClick">
                {{ t(option.textKey) }}
              </MenuItem>
            </template>
            <Menu>
              <template v-if="otherAccounts.length">
                <MenuHeading borderless>
                  {{ t('sidebar_switch_account') }}
                </MenuHeading>
                <MenuItem
                  v-for="account in otherAccounts"
                  :key="account.uid"
                  icon-left="user-2"
                  @click="switchTo(account.uid)"
                >
                  {{ account.name || account.email }}
                </MenuItem>
                <MenuDivider />
              </template>
              <MenuItem icon-left="user-switch" @click="addAnotherAccount">
                {{ t('account_login_another') }}
              </MenuItem>
              <template v-if="isImpersonating">
                <MenuDivider />
                <MenuItem icon-left="sign-out" @click="stopImpersonating">
                  {{ t('account_stop_impersonating') }}
                </MenuItem>
              </template>
            </Menu>
          </Dropdown>
          <MenuItem v-else :icon-left="option.icon" :selected="option.selected" @click="selectOption(index)">
            {{ t(option.textKey) }}
          </MenuItem>
        </template>
      </Menu>
      <LayoutGap direction="column" size="none" class="sidebar__footer">
        <MenuDivider />
        <Menu finish="primary" class="sidebar__menu">
          <MenuItem icon-left="help" @click="handleHelp">
            {{ t('sidebar_help_center') }}
          </MenuItem>
          <MenuItem icon-left="comments" @click="handleStoryCraftCafe">
            {{ t('sidebar_story_craft_cafe') }}
          </MenuItem>
          <MenuItem icon-left="send" @click="requestAFeature">
            {{ t('sidebar_request_feature') }}
          </MenuItem>
        </Menu>
      </LayoutGap>
    </LayoutGap>
  </div>
</template>

<style lang="scss" scoped>
.sidebar {
  width: 250px;
  display: flex;
  flex-direction: column;
  padding: space('small');
  box-sizing: border-box;
}

.sidebar__inner {
  flex: 1;
  min-height: 0;
  width: 100%;
}

.sidebar__menu {
  width: 100%;
}

.sidebar__footer {
  margin-top: auto;
  padding-top: space('small');
  width: 100%;
}

.sidebar__impersonate {
  display: flex;
  padding: space('xxsmall') space('xsmall');
  margin-bottom: space('xxsmall');
  background: none;
  border: none;
  cursor: pointer;
  font: inherit;
  color: inherit;
  text-align: left;
}
</style>
